<script setup>
// Phase 2.9.8 K*.2 — config UI pras Quick Actions. Manager estilo outros
// da aba Kanban (FunnelsManager, OffersManager). Lista + form inline pra
// criar/editar. Form builder pra prompts + steps.
import { ref, computed, onMounted } from 'vue';
import QuickActionsAPI from 'dashboard/api/quickActions';

const items = ref([]);
const voipEnabled = ref(false);
const availableSteps = ref({ general: [], voip: [] });
const editing = ref(null);
const saving = ref(false);
const loading = ref(true);

// === Metadata humana pros tipos de step ===
const STEP_LABELS = {
  kanban_move_stage: '➡ Mover card pra estágio',
  kanban_set_value: '💰 Setar valor do card',
  kanban_set_status: '🏷 Setar status do card (won/lost/etc)',
  kanban_add_note: '📝 Adicionar nota ao card',
  add_label: '🏷 Adicionar etiqueta ao contato',
  remove_label: '🏷❌ Remover etiqueta',
  assign_agent: '👤 Atribuir agente à conversa',
  set_priority: '⚠ Mudar prioridade',
  set_conversation_status: '📂 Mudar status da conversa',
  set_custom_attribute: '📎 Setar atributo custom',
  send_message: '💬 Enviar mensagem',
  // VOIP-only (só aparecem se voipEnabled)
  mark_appointment_attended: '✅ Marcar agendamento como compareceu [VOIP]',
  mark_appointment_no_show: '❌ Marcar agendamento como não compareceu [VOIP]',
  mark_appointment_cancelled: '🚫 Marcar agendamento como cancelado [VOIP]',
  mark_as_won: '🎉 Marcar chamada como venda fechada [VOIP]',
  mark_as_lost: '😕 Marcar chamada como perdida [VOIP]',
};

const PROMPT_TYPE_LABELS = {
  text: 'Texto',
  number: 'Número',
  enum: 'Lista fixa',
};

const COLORS = [
  { value: 'emerald', label: 'Verde', bg: 'bg-green-500' },
  { value: 'blue', label: 'Azul', bg: 'bg-blue-500' },
  { value: 'violet', label: 'Roxo', bg: 'bg-violet-500' },
  { value: 'amber', label: 'Âmbar', bg: 'bg-amber-500' },
  { value: 'rose', label: 'Rosa', bg: 'bg-rose-500' },
  { value: 'red', label: 'Vermelho', bg: 'bg-red-500' },
  { value: 'slate', label: 'Cinza', bg: 'bg-n-slate-9' },
];

const PRESET_ICONS = [
  '✅',
  '❌',
  '💰',
  '📅',
  '📞',
  '🎉',
  '🔥',
  '⚠',
  '⏰',
  '🏆',
  '😕',
  '🚫',
  '⭐',
  '💎',
  '🎯',
];

async function load() {
  loading.value = true;
  try {
    const { data } = await QuickActionsAPI.get();
    items.value = data.quick_actions || [];
    availableSteps.value = data.available_steps || { general: [], voip: [] };
    voipEnabled.value = !!data.voip_enabled;
  } finally {
    loading.value = false;
  }
}
onMounted(load);

function startNew() {
  editing.value = {
    name: '',
    icon: '⚡',
    color: 'emerald',
    enabled: true,
    position: items.value.length,
    show_in_conversation: true,
    show_in_kanban_detail: true,
    show_in_kanban_agenda: true,
    prompts: [],
    steps: [{ type: 'add_label', params: { label: '' } }],
  };
}
function startEdit(qa) {
  editing.value = JSON.parse(JSON.stringify(qa));
}
function cancel() {
  editing.value = null;
}

// Prompts
function addPrompt() {
  editing.value.prompts.push({
    key: '',
    label: '',
    type: 'text',
    required: false,
    options: [],
  });
}
function removePrompt(i) {
  editing.value.prompts.splice(i, 1);
}
function addPromptOption(p) {
  p.options = p.options || [];
  p.options.push('');
}
function removePromptOption(p, i) {
  p.options.splice(i, 1);
}

// Steps
function addStep() {
  editing.value.steps.push({ type: 'add_label', params: { label: '' } });
}
function removeStep(i) {
  editing.value.steps.splice(i, 1);
}

// Disponibilidade de steps baseada em VOIP enabled
const allStepTypes = computed(() => {
  const general = availableSteps.value.general || [];
  const voip = voipEnabled.value ? availableSteps.value.voip || [] : [];
  return [...general, ...voip];
});

// Inputs específicos por step type
function stepInputs(step) {
  const t = step.type;
  step.params = step.params || {};
  if (t === 'kanban_move_stage')
    return [
      { key: 'stage', label: 'Nome da stage (ex: Fechou)', type: 'text' },
    ];
  if (t === 'kanban_set_value')
    return [
      { key: 'value', label: 'Valor (número ou {{prompt}})', type: 'text' },
    ];
  if (t === 'kanban_set_status')
    return [{ key: 'status', label: 'Status (won/lost/etc)', type: 'text' }];
  if (t === 'kanban_add_note')
    return [{ key: 'text', label: 'Texto da nota', type: 'textarea' }];
  if (t === 'add_label' || t === 'remove_label')
    return [{ key: 'label', label: 'Etiqueta', type: 'text' }];
  if (t === 'assign_agent')
    return [{ key: 'user_id', label: 'ID do usuário', type: 'number' }];
  if (t === 'set_priority')
    return [
      {
        key: 'priority',
        label: 'Prioridade (low/medium/high/urgent)',
        type: 'text',
      },
    ];
  if (t === 'set_conversation_status')
    return [
      {
        key: 'status',
        label: 'Status (open/pending/resolved/snoozed)',
        type: 'text',
      },
    ];
  if (t === 'set_custom_attribute')
    return [
      { key: 'attr', label: 'Nome do atributo', type: 'text' },
      { key: 'value', label: 'Valor', type: 'text' },
    ];
  if (t === 'send_message')
    return [{ key: 'content', label: 'Texto', type: 'textarea' }];
  if (t === 'mark_as_won')
    return [
      {
        key: 'value_cents',
        label: 'Valor (opcional, ou {{prompt}})',
        type: 'text',
      },
    ];
  if (t === 'mark_as_lost')
    return [
      {
        key: 'reason',
        label: 'Motivo (opcional, ou {{prompt}})',
        type: 'text',
      },
    ];
  return [];
}

const canSave = computed(
  () =>
    editing.value?.name?.trim() &&
    editing.value?.steps?.length > 0 &&
    editing.value?.steps?.every(s => s.type) &&
    !saving.value
);

async function save() {
  saving.value = true;
  try {
    if (editing.value.id) {
      await QuickActionsAPI.update(editing.value.id, editing.value);
    } else {
      await QuickActionsAPI.create(editing.value);
    }
    await load();
    editing.value = null;
  } catch (e) {
    alert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}

async function remove(qa) {
  if (!confirm(`Deletar ação "${qa.name}"?`)) return;
  await QuickActionsAPI.delete(qa.id);
  await load();
}

async function toggle(qa) {
  await QuickActionsAPI.update(qa.id, { ...qa, enabled: !qa.enabled });
  await load();
}
</script>

<template>
  <div class="h-full w-full overflow-y-auto bg-n-solid-1">
    <div class="max-w-4xl mx-auto p-6">
      <header class="mb-5">
        <h1 class="text-2xl font-extrabold text-n-slate-12">Ações rápidas</h1>
        <p class="text-sm text-n-slate-11 mt-1 leading-snug">
          Botões personalizados que aparecem na barra lateral da conversa, no
          detalhe do card Kanban e na agenda. Agente clica e executa uma cascata
          de ações configurada aqui (mover estágio, etiquetar, registrar venda,
          etc).
          <span
            v-if="voipEnabled"
            class="text-emerald-600 dark:text-emerald-400"
            >· VOIP ativo (ações extras disponíveis)</span>
          <span v-else
class="text-n-slate-11"
            >· VOIP desativado (só ações gerais)</span>
        </p>
      </header>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-10 text-sm text-n-slate-11">
        Carregando...
      </div>

      <!-- Editor -->
      <div
        v-else-if="editing"
        class="bg-n-solid-2 rounded-xl border-2 border-woot-500/40 p-5 space-y-4 mb-6"
      >
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-extrabold text-n-slate-12">
            {{ editing.id ? 'Editar ação' : 'Nova ação rápida' }}
          </h2>
          <button
            class="text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="cancel"
          >
            ✕ Cancelar
          </button>
        </div>

        <!-- Nome + Icon + Color -->
        <div class="grid grid-cols-12 gap-3">
          <div class="col-span-6">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome *</label>
            <input
              v-model="editing.name"
              type="text"
              placeholder="Ex: Compareceu"
              class="w-full rounded border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
            />
          </div>
          <div class="col-span-3">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Ícone</label>
            <div
              class="flex flex-wrap gap-1 p-1 border border-n-weak rounded bg-n-solid-3"
            >
              <button
                v-for="ico in PRESET_ICONS"
                :key="ico"
                type="button"
                class="w-7 h-7 rounded hover:bg-n-alpha-2 text-lg"
                :class="
                  editing.icon === ico ? 'bg-woot-100 dark:bg-woot-800' : ''
                "
                @click="editing.icon = ico"
              >
                {{ ico }}
              </button>
            </div>
          </div>
          <div class="col-span-3">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Cor</label>
            <select
              v-model="editing.color"
              class="w-full rounded border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
            >
              <option v-for="c in COLORS" :key="c.value" :value="c.value">
                {{ c.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Visibilidade -->
        <div class="border-t border-n-weak pt-3">
          <label class="block text-xs font-medium text-n-slate-11 mb-2">Aparecer em:</label>
          <div class="flex flex-wrap gap-4 text-sm">
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="editing.show_in_conversation" type="checkbox" />
              Sidebar da conversa
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="editing.show_in_kanban_detail" type="checkbox" />
              Detalhe do card Kanban
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="editing.show_in_kanban_agenda" type="checkbox" />
              Agenda do Kanban
            </label>
          </div>
        </div>

        <!-- Prompts (opcional) -->
        <div class="border-t border-n-weak pt-3">
          <label class="block text-xs font-medium text-n-slate-11 mb-2">
            Perguntas antes de executar (opcional)
          </label>
          <p class="text-[11px] text-n-slate-11 italic mb-2">
            Modal aparece quando clica. Use
            <code class="bg-n-alpha-2 px-1 rounded">&#123;&#123;key&#125;&#125;</code>
            nos steps pra usar o valor preenchido.
          </p>
          <div
            v-for="(p, i) in editing.prompts"
            :key="i"
            class="rounded border border-n-weak p-2 mb-2 space-y-1.5"
          >
            <div class="flex gap-2">
              <input
                v-model="p.key"
                type="text"
                placeholder="chave (ex: value)"
                class="w-32 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              />
              <input
                v-model="p.label"
                type="text"
                placeholder="Pergunta (ex: Qual o valor?)"
                class="flex-1 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              />
              <select
                v-model="p.type"
                class="w-28 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              >
                <option
                  v-for="(lbl, k) in PROMPT_TYPE_LABELS"
                  :key="k"
                  :value="k"
                >
                  {{ lbl }}
                </option>
              </select>
              <label class="flex items-center gap-1 text-xs"><input
                  v-model="p.required"
                  type="checkbox"
                />Obrigatório</label>
              <button class="text-rose-500 text-xs" @click="removePrompt(i)">
                ✕
              </button>
            </div>
            <div v-if="p.type === 'enum'" class="pl-2">
              <p class="text-[10px] text-n-slate-11 mb-1">Opções da lista:</p>
              <div
                v-for="(_, oi) in p.options || []"
                :key="oi"
                class="flex gap-1 mb-1"
              >
                <input
                  v-model="p.options[oi]"
                  type="text"
                  placeholder="Ex: Preço"
                  class="flex-1 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
                />
                <button
                  class="text-rose-500 text-xs"
                  @click="removePromptOption(p, oi)"
                >
                  ✕
                </button>
              </div>
              <button
                class="text-xs text-woot-500 hover:underline"
                @click="addPromptOption(p)"
              >
                + opção
              </button>
            </div>
          </div>
          <button
            class="text-xs text-woot-500 hover:underline"
            @click="addPrompt"
          >
            + pergunta
          </button>
        </div>

        <!-- Steps (cadeia de ações) -->
        <div class="border-t border-n-weak pt-3">
          <label class="block text-xs font-medium text-n-slate-11 mb-2">Ações (executam em sequência) *</label>
          <div
            v-for="(s, i) in editing.steps"
            :key="i"
            class="rounded border border-n-weak bg-n-alpha-2/30 p-2 mb-2 space-y-1.5"
          >
            <div class="flex items-center gap-2">
              <select
                v-model="s.type"
                class="flex-1 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              >
                <option v-for="t in allStepTypes" :key="t" :value="t">
                  {{ STEP_LABELS[t] || t }}
                </option>
              </select>
              <button class="text-rose-500 text-xs" @click="removeStep(i)">
                ✕
              </button>
            </div>
            <div
              v-for="inp in stepInputs(s)"
              :key="inp.key"
              class="flex items-center gap-2 pl-2"
            >
              <label class="text-[11px] text-n-slate-11 w-20 shrink-0">{{
                inp.label
              }}</label>
              <textarea
                v-if="inp.type === 'textarea'"
                v-model="s.params[inp.key]"
                rows="2"
                class="flex-1 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              />
              <input
                v-else
                v-model="s.params[inp.key]"
                :type="inp.type"
                class="flex-1 rounded border border-n-weak bg-n-solid-3 px-2 py-1 text-xs"
              />
            </div>
          </div>
          <button
            class="text-xs text-woot-500 hover:underline"
            @click="addStep"
          >
            + ação
          </button>
        </div>

        <div class="flex items-center gap-2 pt-3 border-t border-n-weak">
          <button
            class="px-4 py-2 rounded bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50"
            :disabled="!canSave"
            @click="save"
          >
            {{ saving ? '...' : editing.id ? 'Salvar' : 'Criar' }}
          </button>
          <button
            class="px-4 py-2 rounded border border-n-weak text-sm"
            @click="cancel"
          >
            Cancelar
          </button>
        </div>
      </div>

      <!-- Botão novo -->
      <div v-else class="mb-5">
        <button
          class="px-4 py-2 rounded bg-woot-500 text-white text-sm font-medium hover:bg-woot-600"
          @click="startNew"
        >
          + Nova ação rápida
        </button>
      </div>

      <!-- Lista -->
      <div v-if="!editing && !loading && items.length" class="space-y-2">
        <div
          v-for="qa in items"
          :key="qa.id"
          class="bg-n-solid-2 rounded-xl border border-n-weak p-3 flex items-center gap-3"
        >
          <span class="text-2xl">{{ qa.icon }}</span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ qa.name }}
            </p>
            <p class="text-[11px] text-n-slate-11">
              {{ qa.steps.length }} ação{{
                qa.steps.length !== 1 ? 'ões' : ''
              }}
              · {{ qa.prompts.length }} pergunta{{
                qa.prompts.length !== 1 ? 's' : ''
              }}
              <template v-if="qa.show_in_conversation"> · 💬 conversa</template>
              <template v-if="qa.show_in_kanban_detail"> · 📋 kanban</template>
              <template v-if="qa.show_in_kanban_agenda"> · 📅 agenda</template>
            </p>
          </div>
          <label class="flex items-center gap-1 text-xs cursor-pointer">
            <input type="checkbox" :checked="qa.enabled" @change="toggle(qa)" />
            {{ qa.enabled ? 'Ativa' : 'Pausada' }}
          </label>
          <button
            class="text-xs text-woot-500 hover:underline"
            @click="startEdit(qa)"
          >
            Editar
          </button>
          <button
            class="text-xs text-rose-500 hover:underline"
            @click="remove(qa)"
          >
            🗑
          </button>
        </div>
      </div>
      <p
        v-else-if="!editing && !loading"
        class="text-sm text-n-slate-11 text-center py-8 italic"
      >
        Nenhuma ação rápida ainda. Clique em "+ Nova ação rápida" pra começar.
      </p>
    </div>
  </div>
</template>
